import { TAdFoxBannerCompleteSizes } from '@cian/adfox-component';
import { UIHeading2, UIHeading4, Label } from '@cian/ui-kit';
import { clsx } from 'clsx';
import * as React from 'react';

import styles from './NewbuildingSpecialProjectAnimated.css';
import { Marquee } from '../../../../../../../packages/Marquee';

import type { INewbuildingSpecialProjectBannerAnimateContext } from '../../../../../../types/adfox';

interface INewbuildingSpecialProjectAnimatedProps {
  sizes: TAdFoxBannerCompleteSizes;
  context: INewbuildingSpecialProjectBannerAnimateContext;
}

export const NewbuildingSpecialProjectAnimated: React.FC<INewbuildingSpecialProjectAnimatedProps> = ({
  context,
  sizes,
}) => {
  const { title, text, url, buttonText, substrateNotNeeded, substrateLogoNotNeeded, disclaimer, logo, bgImage, label } =
    context;

  const containerRef = React.useRef<HTMLAnchorElement>(null);
  const backgroundRef = React.useRef<HTMLDivElement>(null);
  const rafIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (backgroundRef.current) {
      backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
    }

    const updateParallax = () => {
      if (!containerRef.current || !backgroundRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;
      const elementCenter = elementTop + elementHeight / 2;
      const viewportCenter = windowHeight / 2;

      if (elementTop < windowHeight && elementTop + elementHeight > 0) {
        const distanceFromCenter = (elementCenter - viewportCenter) / windowHeight;
        const parallaxOffset = distanceFromCenter * 80;
        backgroundRef.current.style.transform = `translate3d(0px, ${parallaxOffset}px, 0px)`;
      } else {
        backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
      }
    };

    const handleScroll = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updateParallax);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateParallax();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return (
    <div className={styles['block-wrapper']} data-testid="NewbuildingSpecialProjectAnimated">
      <a
        ref={containerRef}
        href={url}
        target="_blank"
        rel="nofollow noopener noreferrer"
        className={clsx(
          styles['container'],
          !Number(substrateNotNeeded) && styles['container--without-substrate'],
          !Number(substrateLogoNotNeeded) && styles['container--without-substratelogo'],
        )}
        style={sizes}
      >
        <div ref={backgroundRef} className={styles['background-layer']}>
          <img src={bgImage} alt="image" className={styles['background-image']} data-testid="background-image" />
        </div>
        <div className={styles['wrapper']}>
          <div className={styles['logo']}>
            <img className={styles['logo-image']} src={logo} alt={title} data-testid="logo" />
          </div>
          <div className={styles['content']}>
            <div className={styles['info']}>
              <div className={styles['title']} data-testid="title">
                {title}
              </div>
              <div className={styles['text']}>
                <UIHeading2 color="text-inverted-default" data-testid="text">
                  {text}
                </UIHeading2>
              </div>
            </div>

            <div className={styles['control']}>
              {!!label && (
                <div className={styles['control-label-wrapper']}>
                  <Label data-testid="label" background="overlay-default" color="text-inverted-default">
                    {label}
                  </Label>
                </div>
              )}

              {!!buttonText && (
                <button type="button" className={styles['action-button']} data-testid="action-button">
                  <span className={styles['action-button-text']}>
                    <UIHeading4 color="text-inverted-default">{buttonText}</UIHeading4>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
        {disclaimer && (
          <div className={styles['note']}>
            <Marquee text={disclaimer} textPadding="s" />
          </div>
        )}
      </a>
    </div>
  );
};
